import { Fragment, ReactElement, Ref, cloneElement, useCallback } from 'react';

import { Action } from '../../apis';
import { useImpressionTracker, useInteractionTracker } from '../../hooks';
import { ElementId, TrackingData } from '../../types';

export interface InteractionTrackerProps {
  elementId: ElementId;
  action: Action;
  data?: TrackingData;
  children: ReactElement;
  hover?: boolean;
  disableClick?: boolean;
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref && typeof ref === 'object') {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

export function InteractionTracker({
  elementId,
  action,
  data = {},
  children,
  hover = false,
  disableClick = false,
}: InteractionTrackerProps) {
  if (process.env.NODE_ENV === 'development' && children.type === Fragment) {
    // eslint-disable-next-line no-console
    console.error(
      'InteractionTracker: children must be a single DOM element, not a Fragment. ' +
        'Wrap the children in a container element so event handlers can be attached.',
    );
  }

  const track = useInteractionTracker({ elementId, action, data });
  const impressionRef = useImpressionTracker({ elementId, data });
  const childRef = (children as any).ref;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mergedRef = useCallback(
    (node: Element | null) => {
      setRef(impressionRef as Ref<Element>, node);
      setRef(childRef, node);
    },
    [impressionRef, childRef],
  );

  const onMouseEnter = (...args: any[]) => {
    if (hover) {
      track({ type: 'hover' });
    }
    return children.props.onMouseEnter?.(...args);
  };

  const onClick = (...args: any[]) => {
    if (!disableClick) {
      track({ type: 'click' });
    }

    return children.props.onClick?.(...args);
  };

  return cloneElement(children, {
    ...children.props,
    ref: mergedRef,
    onClick,
    onMouseEnter,
  });
}
